import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { BreadcrumbItem } from '../components/ui/Breadcrumb';

const routeLabels: Record<string, string> = {
  '/app/dashboard': 'Dashboard',
  '/app/users': 'Users',
  '/app/service-accounts': 'Service Accounts',
  '/app/groups': 'Groups',
  '/app/roles': 'Roles',
  '/app/applications': 'Applications',
  '/app/app-jobs': 'App Jobs',
  '/app/background-jobs': 'Background Jobs',
  '/app/volumes': 'Volumes',
  '/app/database-connections': 'Database Connections',
  '/app/document-databases': 'Document Databases',
  '/app/managed-sql-databases': 'Managed SQL Databases',
  '/app/secrets': 'Secrets',
  '/app/data-agents': 'Data Agents',
  '/app/elt-jobs': 'ELT Jobs',
  '/app/job-executions': 'Job Executions',
  '/app/marketplace': 'Marketplace',
  '/app/marketplace-installed': 'Installed Apps',
  '/app/partner-dashboard': 'Partner Dashboard',
  '/app/launcher': 'Launcher',
  '/app/support': 'Support',
  '/app/profile': 'Profile',
  '/app/settings': 'Settings',
  '/app/settings/sso': 'SSO Configuration',
  '/app/billing': 'Billing',
  '/app/inbox': 'Inbox',
};

const routeParents: Record<string, string> = {
  '/app/dashboard': '/app/dashboard',
  '/app/users': '/app/dashboard',
  '/app/service-accounts': '/app/dashboard',
  '/app/groups': '/app/dashboard',
  '/app/roles': '/app/dashboard',
  '/app/applications': '/app/dashboard',
  '/app/app-jobs': '/app/dashboard',
  '/app/background-jobs': '/app/dashboard',
  '/app/volumes': '/app/dashboard',
  '/app/database-connections': '/app/dashboard',
  '/app/document-databases': '/app/dashboard',
  '/app/managed-sql-databases': '/app/dashboard',
  '/app/secrets': '/app/dashboard',
  '/app/data-agents': '/app/dashboard',
  '/app/elt-jobs': '/app/dashboard',
  '/app/job-executions': '/app/dashboard',
  '/app/marketplace': '/app/dashboard',
  '/app/marketplace-installed': '/app/marketplace',
  '/app/partner-dashboard': '/app/marketplace',
  '/app/launcher': '/app/dashboard',
  '/app/support': '/app/dashboard',
  '/app/profile': '/app/dashboard',
  '/app/settings': '/app/dashboard',
  '/app/settings/sso': '/app/settings',
  '/app/billing': '/app/dashboard',
  '/app/inbox': '/app/dashboard',
};

/**
 * Builds a breadcrumb chain by walking from the current path up through parents.
 * The current page (last item) has no href; all parent items get href.
 */
export const useBreadcrumbs = (): BreadcrumbItem[] => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const chain: BreadcrumbItem[] = [];
    let current = pathname;

    // Walk up the parent chain collecting items
    const visited = new Set<string>();

    while (current && routeLabels[current] && !visited.has(current)) {
      visited.add(current);
      chain.unshift({
        label: routeLabels[current],
      });

      const parent = routeParents[current];
      if (!parent || parent === current) {
        break;
      }

      current = parent;
    }

    // Assign href to all items except the last (current page)
    return chain.map((item, index) => {
      const isLast = index === chain.length - 1;
      if (isLast) {
        return item;
      }

      // Find the path for this label
      const path = Object.entries(routeLabels).find(
        ([, label]) => label === item.label
      )?.[0];

      return {
        ...item,
        href: path,
      };
    });
  }, [pathname]);
};
